import type { ProductLawKey } from '@/shared/hooks/api/product'
import { summarizeRequiredBriefingSteps } from '@/shared/briefing/summarizeRequiredBriefingSteps'
import type { EmcProductComplianceFilesState } from '@/shared/hooks/api/emcComplianceFiles'
import type {
  EmcProductComplianceFileEvidenceSlot,
  EmcComplianceFileSlotAttachmentsView,
} from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import type { LvdProductComplianceFilesState } from '@/shared/hooks/api/lvdComplianceFiles'
import type {
  LvdProductComplianceFileEvidenceSlot,
  LvdComplianceFileSlotAttachmentsView,
} from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import type { ProductSharedEvidenceView } from '@/shared/hooks/api/productSharedEvidence'
import type { ProductDrawingsView } from '@/shared/hooks/api/rohsProductDrawings'
import type { RohsProductComplianceFilesState } from '@/shared/hooks/api/rohsComplianceFiles'
import { isProductSharedEvidenceSlotFilled } from '@/shared/hooks/api/productSharedEvidence'
import { isProductDrawingsFilled } from '@/shared/hooks/api/rohsProductDrawings'
import { isLvdComplianceFileSlotFilled } from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import { isEmcComplianceFileSlotFilled } from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { isEmcTechnicalDrawingsEvidenceFilled } from '@/shared/lib/emcTechnicalDrawingsEvidence'
import { buildEmcLegislationBriefing } from './emcLegislationBriefing'
import { buildLvdLegislationBriefing } from './lvdLegislationBriefing'
import { buildRohsLegislationBriefing } from './rohsLegislationBriefing'

export const REGULATION_JOURNEY_TRACKED_LAWS = [
  'rohs',
  'lvd',
  'emc',
] as const satisfies readonly ProductLawKey[]

export function isRegulationJourneyTrackedLaw(
  law: ProductLawKey
): law is (typeof REGULATION_JOURNEY_TRACKED_LAWS)[number] {
  return (REGULATION_JOURNEY_TRACKED_LAWS as readonly ProductLawKey[]).includes(
    law
  )
}

export type RegulationJourneySummary = {
  completeCount: number
  totalCount: number
  isComplete: boolean
}

export type ProductRegulationsJourneyByLaw = Partial<
  Record<ProductLawKey, RegulationJourneySummary>
>

function hasProductDescription(
  description: string | null | undefined
): boolean {
  return description != null && description.trim() !== ''
}

export function buildProductRegulationsJourneyByLaw(input: {
  productDescription: string | null | undefined
  sharedEvidence: ProductSharedEvidenceView | undefined
  rohsApplies: boolean
  rohsDrawings: ProductDrawingsView | undefined
  rohsFiles: RohsProductComplianceFilesState | undefined
  lvdApplies: boolean
  lvdFiles: LvdProductComplianceFilesState | undefined
  lvdSlotAttachments: Partial<
    Record<
      LvdProductComplianceFileEvidenceSlot,
      LvdComplianceFileSlotAttachmentsView | undefined
    >
  >
  emcApplies: boolean
  emcFiles: EmcProductComplianceFilesState | undefined
  emcSlotAttachments: Partial<
    Record<
      EmcProductComplianceFileEvidenceSlot,
      EmcComplianceFileSlotAttachmentsView | undefined
    >
  >
}): ProductRegulationsJourneyByLaw {
  const journey: ProductRegulationsJourneyByLaw = {}
  const hasDescription = hasProductDescription(input.productDescription)
  const hasProductIdentification = isProductSharedEvidenceSlotFilled(
    input.sharedEvidence,
    'PRODUCT_IDENTIFICATION'
  )
  const hasManufacturerInformation = isProductSharedEvidenceSlotFilled(
    input.sharedEvidence,
    'MANUFACTURER_INFORMATION'
  )

  if (input.rohsApplies && input.rohsFiles != null) {
    journey.rohs = summarizeRequiredBriefingSteps(
      buildRohsLegislationBriefing({
        hasProductDescription: hasDescription,
        hasProductIdentification,
        hasManufacturerInformation,
        hasProductDrawings: isProductDrawingsFilled(input.rohsDrawings),
        files: input.rohsFiles,
      }).steps
    )
  }

  if (input.lvdApplies && input.lvdFiles != null) {
    journey.lvd = summarizeRequiredBriefingSteps(
      buildLvdLegislationBriefing({
        hasProductDescription: hasDescription,
        hasProductIdentification,
        hasManufacturerInformation,
        slotFilled: {
          DRAWINGS_SCHEMAS: isLvdComplianceFileSlotFilled(
            input.lvdSlotAttachments.DRAWINGS_SCHEMAS
          ),
          STANDARDS_TEST_REPORT: isLvdComplianceFileSlotFilled(
            input.lvdSlotAttachments.STANDARDS_TEST_REPORT
          ),
          EXAMINATIONS_OR_CALCULATIONS: isLvdComplianceFileSlotFilled(
            input.lvdSlotAttachments.EXAMINATIONS_OR_CALCULATIONS
          ),
          INSTRUCTIONS_AND_SAFETY_INFORMATION: isLvdComplianceFileSlotFilled(
            input.lvdSlotAttachments.INSTRUCTIONS_AND_SAFETY_INFORMATION
          ),
        },
        files: input.lvdFiles,
      }).steps
    )
  }

  if (input.emcApplies && input.emcFiles != null) {
    journey.emc = summarizeRequiredBriefingSteps(
      buildEmcLegislationBriefing({
        hasProductDescription: hasDescription,
        hasProductIdentification,
        hasManufacturerInformation,
        slotFilled: {
          DRAWINGS_SCHEMAS: isEmcTechnicalDrawingsEvidenceFilled(
            input.emcSlotAttachments.DRAWINGS_SCHEMAS,
            input.emcSlotAttachments.DESCRIPTIONS_AND_EXPLANATIONS
          ),
          TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE: isEmcComplianceFileSlotFilled(
            input.emcSlotAttachments.TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE
          ),
          TEST_REPORT_ELECTROMAGNETIC_IMMUNITY: isEmcComplianceFileSlotFilled(
            input.emcSlotAttachments.TEST_REPORT_ELECTROMAGNETIC_IMMUNITY
          ),
          DESCRIPTIONS_AND_EXPLANATIONS: isEmcComplianceFileSlotFilled(
            input.emcSlotAttachments.DESCRIPTIONS_AND_EXPLANATIONS
          ),
        },
        files: input.emcFiles,
      }).steps
    )
  }

  return journey
}
